/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockItem } from "../Stock/types";
import { unitOptions } from "../Stock/units";
import type { SupplierItem } from "./types";
import { PriceSparkline } from "./PriceSparkline";

export type SupplierItemPatch = {
  id: number;
  name: string;
  stock_item_id: number | null;
  unit: string | null;
};

type Props = {
  item: SupplierItem;
  stockItems: StockItem[];
  saving: boolean;
  onSave: (patch: SupplierItemPatch) => void;
};

// One supplier item: its name (renames keep the old one as an alias), what
// it is in stock (item + unit) and how its price has moved.
export const SupplierItemRow = defineComponent<Props>({
  name: "SupplierItemRow",
  props: ["item", "stockItems", "saving", "onSave"],
  setup(props) {
    const { t } = useLocalization();
    const name = ref(props.item.name);
    const stockItemId = ref<number | null>(props.item.stock_item_id);
    const unit = ref<string | null>(props.item.unit);

    watch(
      () => props.item,
      (item) => {
        name.value = item.name;
        stockItemId.value = item.stock_item_id;
        unit.value = item.unit;
      },
    );

    const dirty = () =>
      name.value.trim() !== props.item.name ||
      stockItemId.value !== props.item.stock_item_id ||
      unit.value !== props.item.unit;

    return () => {
      const stock = props.stockItems.find((s) => s.id === stockItemId.value);
      const change = props.item.price_change;
      return (
        <tr>
          <td>
            <input class="input input-bordered input-sm w-full min-w-40" v-model={name.value} />
            {props.item.aliases.length > 0 && (
              <div class="mt-1 text-xs text-base-content/50">
                {t("inventory.suppliers.also_known_as").replace("{names}", props.item.aliases.join(", "))}
              </div>
            )}
          </td>
          <td>
            <select
              class="select select-bordered select-sm w-full min-w-36"
              value={stockItemId.value ?? ""}
              onChange={(e: Event) => {
                const v = (e.target as HTMLSelectElement).value;
                stockItemId.value = v ? Number(v) : null;
                const next = props.stockItems.find((s) => s.id === stockItemId.value);
                unit.value = next ? next.base_unit : null;
              }}
            >
              <option value="">{t("inventory.suppliers.not_mapped")}</option>
              {props.stockItems.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </td>
          <td>
            <select
              class="select select-bordered select-sm w-24"
              disabled={!stock}
              value={unit.value ?? ""}
              onChange={(e: Event) => (unit.value = (e.target as HTMLSelectElement).value)}
            >
              {stock && unitOptions(stock).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </td>
          <td class="whitespace-nowrap text-right">
            <div class="font-medium">{props.item.last_price == null ? "—" : props.item.last_price.toFixed(2)}</div>
            {change != null && (
              <div class={["text-xs", change > 0 ? "text-warning" : change < 0 ? "text-success" : "text-base-content/50"].join(" ")}>
                {`${change > 0 ? "+" : ""}${change.toFixed(1)}%`}
              </div>
            )}
          </td>
          <td>
            <PriceSparkline prices={props.item.prices.map((p) => p.price)} />
          </td>
          <td class="text-right">
            <button
              class="btn btn-ghost btn-xs"
              disabled={props.saving || !dirty() || !name.value.trim()}
              onClick={() =>
                props.onSave({
                  id: props.item.id,
                  name: name.value.trim(),
                  stock_item_id: stockItemId.value,
                  unit: stockItemId.value ? unit.value : null,
                })
              }
            >
              {t("general.words.save")}
            </button>
          </td>
        </tr>
      );
    };
  },
});
